// ═══════════════════════════════════════════════════════════════
// POLLEN CARD — SYBNT (Some Year But Not This)
// Path: src/components/dashboard/pollen/PollenSYBNT.jsx
//
// "They gave once. They might again."
// SYBNT donors: gave in some fiscal year BEFORE last fiscal year,
// but did NOT give in last fiscal year or current fiscal year.
// (LYBNT donors are excluded — they have their own card.)
//
// Fiscal year boundaries come from chapter_settings.fiscal_year_start_month.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { Clock, X, Info, Sprout, EyeOff } from "lucide-react";
import { useApp } from "../../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../../constants/appConfig";
import { supabase } from "../../../lib/supabase";

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount || 0);

// Compute fiscal year boundaries from the chapter's fiscal start month (1-12)
const getFiscalYearDates = (startMonth) => {
  const safeStart = Math.min(Math.max(parseInt(startMonth, 10) || 1, 1), 12);
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-based

  const currentFyStartYear =
    currentMonth >= safeStart ? currentYear : currentYear - 1;
  const mm = String(safeStart).padStart(2, "0");
  const currentFyStart = `${currentFyStartYear}-${mm}-01`;
  const lastFyStart = `${currentFyStartYear - 1}-${mm}-01`;

  return { currentFyStart, lastFyStart };
};

export const PollenSYBNT = ({ onClose }) => {
  const { palette, navigate, dataVersion, members, cultivationQueue, currentUser, userProfile, chapterSettings } = useApp();
  const [sybntDonors, setSybntDonors] = useState([]);
  const [contactedIds, setContactedIds] = useState(new Set());
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [showInfo, setShowInfo] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);

  // Seed dismissed from profile
  useEffect(() => {
    const saved = userProfile?.pollen_dismissed_sybnt;
    if (Array.isArray(saved) && saved.length > 0) setDismissedIds(new Set(saved));
  }, [userProfile]);

  const persistDismissed = async (newSet) => {
    const arr = [...newSet];
    setDismissedIds(newSet);
    const userId = currentUser?.id;
    if (!userId) return;
    try {
      await supabase
        .from("profiles")
        .update({ pollen_dismissed_sybnt: arr })
        .eq("id", userId);
    } catch (err) {
      console.error("[PollenSYBNT] Persist exception:", err);
    }
  };

  const handleDismiss = (memberId) => {
    const updated = new Set(dismissedIds);
    updated.add(memberId);
    persistDismissed(updated);
  };

  const handleRestore = (memberId) => {
    const updated = new Set(dismissedIds);
    updated.delete(memberId);
    persistDismissed(updated);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const { currentFyStart, lastFyStart } = getFiscalYearDates(chapterSettings?.fiscalYearStartMonth);

        const PAGE_SIZE = 1000;

        const fetchAll = async (query) => {
          const allRows = [];
          let offset = 0;
          while (true) {
            const { data: page, error } = await query().range(offset, offset + PAGE_SIZE - 1);
            if (error || !page) break;
            allRows.push(...page);
            if (page.length < PAGE_SIZE) break;
            offset += PAGE_SIZE;
          }
          return allRows;
        };

        // Paginated queries need a stable .order() — without it Supabase
        // can return overlapping/missing rows across .range() calls,
        // which would let multi-gift donors slip into the wrong bucket.

        // Query A: ALL gifts before last fiscal year start
        const olderGifts = await fetchAll(() =>
          supabase
            .from("gifts")
            .select("member_id, gift_date, amount")
            .lt("gift_date", lastFyStart)
            .order("id", { ascending: true })
        );

        // Query B: Gifts in last fiscal year (these are LYBNT — exclude them)
        const lastFyGifts = await fetchAll(() =>
          supabase
            .from("gifts")
            .select("member_id")
            .gte("gift_date", lastFyStart)
            .lt("gift_date", currentFyStart)
            .order("id", { ascending: true })
        );

        // Query C: Gifts in current fiscal year
        const currentFyGifts = await fetchAll(() =>
          supabase
            .from("gifts")
            .select("member_id")
            .gte("gift_date", currentFyStart)
            .order("id", { ascending: true })
        );

        // Query D: Contact logs since current FY start
        const recentContacts = await fetchAll(() =>
          supabase
            .from("contact_logs")
            .select("member_id")
            .gte("log_date", currentFyStart)
            .order("id", { ascending: true })
        );

        setContactedIds(new Set(recentContacts.map((log) => log.member_id)));

        // Exclude anyone who gave in last FY or current FY
        const gaveLastFy = new Set(lastFyGifts.map((gift) => gift.member_id));
        const gaveCurrentFy = new Set(currentFyGifts.map((gift) => gift.member_id));

        // Group older gifts by member
        const olderByMember = {};
        olderGifts.forEach((gift) => {
          const memberId = gift.member_id;
          if (!olderByMember[memberId]) {
            olderByMember[memberId] = { total: 0, count: 0, lastDate: null };
          }
          olderByMember[memberId].total += Number(gift.amount) || 0;
          olderByMember[memberId].count += 1;
          if (!olderByMember[memberId].lastDate || gift.gift_date > olderByMember[memberId].lastDate) {
            olderByMember[memberId].lastDate = gift.gift_date;
          }
        });

        // SYBNT = gave before last FY, NOT in last FY, NOT in current FY
        const results = Object.keys(olderByMember)
          .filter((memberId) => !gaveLastFy.has(memberId) && !gaveCurrentFy.has(memberId))
          .map((memberId) => {
            const record = olderByMember[memberId];
            return {
              memberId,
              lifetimeTotal: record.total,
              giftCount: record.count,
              lastGiftDate: record.lastDate,
            };
          })
          .sort((a, b) => b.lifetimeTotal - a.lifetimeTotal);

        setSybntDonors(results);
      } catch (err) {
        // silent
      }
    };
    load();
  }, [dataVersion, chapterSettings?.fiscalYearStartMonth]);

  const getDonorName = (donor) => {
    const member = members.find((m) => m.id === donor.memberId);
    if (!member) return "\u2014";
    return (
      member.credentialedFullName ||
      `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
      "\u2014"
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };

  const visibleDonors = sybntDonors.filter((d) => !dismissedIds.has(d.memberId));
  const dismissedDonors = sybntDonors.filter((d) => dismissedIds.has(d.memberId));
  const uncontactedCount = visibleDonors.filter((d) => !contactedIds.has(d.memberId)).length;

  return (
    <div
      style={{
        background: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "none",
          background: palette.background,
          borderRadius: "10px 10px 0 0",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Clock size={ICON_SIZES.sm} style={{ color: "#fff" }} />
          <span
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 700,
              color: "#fff",
              fontFamily: FONT_FAMILY,
            }}
          >
            SYBNT
          </span>
          {sybntDonors.length > 0 && (
            <span
              style={{
                fontSize: FONT_SIZES.xs,
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 6,
                background: "rgba(255,255,255,0.15)",
                color: "#fff",
                fontFamily: FONT_FAMILY,
              }}
            >
              {uncontactedCount} / {visibleDonors.length}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            onClick={() => setShowInfo((prev) => !prev)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 2,
              display: "flex",
              alignItems: "center",
              opacity: showInfo ? 1 : 0.5,
              transition: "opacity 0.15s",
            }}
            title="How this card works"
          >
            <Info size={ICON_SIZES.sm} style={{ color: "rgba(255,255,255,0.6)" }} />
          </button>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 2,
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={ICON_SIZES.sm} style={{ color: "rgba(255,255,255,0.6)" }} />
          </button>
        </div>
      </div>

      {/* Info box */}
      {showInfo && (
        <div
          style={{
            padding: "10px 16px",
            fontSize: FONT_SIZES.xs,
            lineHeight: 1.5,
            color: palette.text2,
            fontFamily: FONT_FAMILY,
            background: `${palette.accent1}06`,
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          <strong>SYBNT</strong> — Some Year But Not This. These donors gave at
          some point in the past but have not given in the last fiscal year or
          the current one. They lapsed longer ago than LYBNT donors. Names
          in <span style={{ color: palette.text3 }}>muted text</span> have
          already been contacted this year. The badge
          shows <strong>uncontacted / total</strong>.
        </div>
      )}

      {/* Subtitle */}
      <div
        style={{
          padding: "8px 16px 4px",
          fontSize: "0.65rem",
          color: palette.text3,
          fontFamily: FONT_FAMILY,
          fontStyle: "italic",
        }}
      >
        Gave before, but not last year or this. They might give again.
      </div>

      {/* Donor list — scrollable */}
      {visibleDonors.length === 0 && dismissedDonors.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: palette.text3,
            fontSize: FONT_SIZES.xs,
            fontFamily: FONT_FAMILY,
          }}
        >
          No SYBNT donors right now.
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "4px 0",
            maxHeight: 320,
            overflowY: "auto",
          }}
        >
          {visibleDonors.map((donor) => {
            const contacted = contactedIds.has(donor.memberId);
            return (
              <div
                key={donor.memberId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 16px",
                  transition: "background 0.1s",
                  opacity: contacted ? 0.55 : 1,
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = `${palette.accent1}08`;
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = "transparent";
                }}
              >
                {/* Dismiss button */}
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDismiss(donor.memberId);
                  }}
                  title="No action needed"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 2,
                    display: "flex",
                    alignItems: "center",
                    opacity: 0.3,
                    transition: "opacity 0.15s",
                    flexShrink: 0,
                  }}
                  onMouseEnter={(event) => { event.currentTarget.style.opacity = "0.8"; }}
                  onMouseLeave={(event) => { event.currentTarget.style.opacity = "0.3"; }}
                >
                  <EyeOff size={ICON_SIZES.xs} style={{ color: palette.text3 }} />
                </button>

                {/* Name — clickable */}
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    cursor: members.find((m) => m.id === donor.memberId) ? "pointer" : "default",
                  }}
                  onClick={() => {
                    const fullMember = members.find((m) => m.id === donor.memberId);
                    if (fullMember)
                      navigate("memberDetail", fullMember, {
                        from: "sybnt",
                        suggestedTemplate: "sybnt",
                      });
                  }}
                >
                  <div
                    style={{
                      fontSize: FONT_SIZES.xs,
                      fontWeight: contacted ? 400 : 600,
                      color: contacted ? palette.text3 : palette.text1,
                      fontFamily: FONT_FAMILY,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textDecoration: contacted ? "line-through" : "none",
                    }}
                  >
                    {getDonorName(donor)}
                  </div>
                </div>
                {cultivationQueue.some((q) => q.memberId === donor.memberId) && (
                  <Sprout size={ICON_SIZES.xs} style={{ color: "#10b981", flexShrink: 0 }} title="In cultivation queue" />
                )}
                {contacted && (
                  <span
                    style={{
                      fontSize: "0.5rem",
                      fontWeight: 600,
                      padding: "1px 5px",
                      borderRadius: 4,
                      background: "#10b98115",
                      color: "#10b981",
                      fontFamily: FONT_FAMILY,
                      flexShrink: 0,
                    }}
                  >
                    contacted
                  </span>
                )}
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    color: palette.text3,
                    fontFamily: FONT_FAMILY,
                    flexShrink: 0,
                  }}
                  title={`Last gift: ${formatDate(donor.lastGiftDate)}`}
                >
                  {formatDate(donor.lastGiftDate)}
                </span>
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 600,
                    color: contacted ? palette.text3 : "#f59e0b",
                    fontFamily: FONT_FAMILY,
                    flexShrink: 0,
                  }}
                >
                  {fmtCurrency(donor.lifetimeTotal)}
                </span>
              </div>
            );
          })}

          {/* Dismissed section */}
          {dismissedDonors.length > 0 && (
            <div
              style={{
                borderTop: `1px solid ${palette.cardBorder}`,
                marginTop: 4,
                paddingTop: 4,
              }}
            >
              <button
                onClick={() => setShowDismissed((prev) => !prev)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  width: "100%",
                  padding: "6px 16px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 600,
                  color: palette.text3,
                  fontFamily: FONT_FAMILY,
                }}
              >
                <EyeOff size={ICON_SIZES.xs} />
                {dismissedDonors.length} dismissed
                <span style={{ marginLeft: 4 }}>{showDismissed ? "\u25BE" : "\u25B8"}</span>
              </button>
              {showDismissed && dismissedDonors.map((donor) => (
                <div
                  key={donor.memberId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 16px",
                    opacity: 0.4,
                  }}
                >
                  <button
                    onClick={() => handleRestore(donor.memberId)}
                    title="Restore to list"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 2,
                      display: "flex",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <X size={ICON_SIZES.xs} style={{ color: palette.text3 }} />
                  </button>
                  <span
                    style={{
                      flex: 1,
                      fontSize: FONT_SIZES.xs,
                      color: palette.text3,
                      fontFamily: FONT_FAMILY,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {getDonorName(donor)}
                  </span>
                  <span
                    style={{
                      fontSize: FONT_SIZES.xs,
                      color: palette.text3,
                      fontFamily: FONT_FAMILY,
                      flexShrink: 0,
                    }}
                  >
                    {fmtCurrency(donor.lifetimeTotal)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
